import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ConsultationPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);

  const isUz = i18n.language === 'uz';

  useEffect(() => {
    const title = isUz 
      ? 'Bepul Konsultatsiya - MediVoyage'
      : 'Бесплатная консультация - MediVoyage';
    const description = isUz
      ? 'Koreyada tibbiy davolanish bo\'yicha bepul konsultatsiya. Mutaxassislarimiz siz bilan tez orada bog\'lanadilar.'
      : 'Бесплатная консультация по медицинскому лечению в Корее. Наши специалисты свяжутся с вами в ближайшее время.';

    document.title = title;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // Schema.org JSON-LD - Service
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.medivoyageuz.com';
    
    const schemaService = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': isUz ? 'Bepul tibbiy konsultatsiya' : 'Бесплатная медицинская консультация',
      'url': `${siteUrl}/consultation`,
      'description': description,
      'provider': {
        '@type': 'MedicalOrganization',
        'name': 'MediVoyage',
        'url': siteUrl,
      },
      'serviceType': 'Medical Consultation',
      'areaServed': {
        '@type': 'Country',
        'name': 'South Korea',
      },
    };

    let scriptService = document.getElementById('schema-service');
    if (!scriptService) {
      scriptService = document.createElement('script');
      scriptService.id = 'schema-service';
      scriptService.type = 'application/ld+json';
      document.head.appendChild(scriptService);
    }
    scriptService.textContent = JSON.stringify(schemaService);

    // Canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = `${siteUrl}/consultation`;

    return () => {
      scriptService?.remove();
    };
  }, [i18n.language, isUz]);

  const labels = {
    pageTitle: isUz ? 'Bepul Konsultatsiya' : 'Бесплатная консультация',
    pageSubtitle: isUz
      ? 'Mutaxassislarimiz siz bilan tez orada bog\'lanadilar'
      : 'Наши специалисты свяжутся с вами в ближайшее время',
    fullName: isUz ? 'To\'liq ism' : 'Полное имя',
    fullNamePlaceholder: isUz ? 'Ismingizni kiriting' : 'Введите ваше имя',
    phone: isUz ? 'Telefon raqami' : 'Номер телефона',
    phonePlaceholder: isUz ? '+998 XX XXX XX XX' : '+7 XXX XXX XX XX',
    emailPlaceholder: isUz ? 'email@example.com' : 'email@example.com',
    country: isUz ? 'Mamlakat' : 'Страна',
    countryPlaceholder: isUz ? 'Mamlakatni tanlang' : 'Выберите страну',
    countryUz: isUz ? 'O\'zbekiston' : 'Узбекистан',
    countryRu: isUz ? 'Rossiya' : 'Россия',
    countryKz: isUz ? 'Qozog\'iston' : 'Казахстан',
    countryOther: isUz ? 'Boshqa' : 'Другое',
    specialty: isUz ? 'Tibbiy yo\'nalish' : 'Направление лечения',
    specialtyPlaceholder: isUz ? 'Yo\'nalishni tanlang' : 'Выберите направление',
    message: isUz ? 'Qo\'shimcha ma\'lumot' : 'Дополнительная информация',
    messagePlaceholder: isUz
      ? 'Kasalligingiz, savollaringiz yoki boshqa ma\'lumotlarni yozing...'
      : 'Опишите ваше состояние, вопросы или другую информацию...',
    submit: isUz ? 'Yuborish' : 'Отправить',
    submitting: isUz ? 'Yuborilmoqda...' : 'Отправка...',
    successTitle: isUz ? 'Muvaffaqiyatli yuborildi!' : 'Успешно отправлено!',
    successMsg: isUz
      ? 'Xabaringiz qabul qilindi. Mutaxassislarimiz 24 soat ichida siz bilan bog\'lanadilar.'
      : 'Ваша заявка принята. Наши специалисты свяжутся с вами в течение 24 часов.',
    backHome: isUz ? 'Bosh sahifaga qaytish' : 'Вернуться на главную',
    sendAnother: isUz ? 'Yana yuborish' : 'Отправить ещё',
    errorMsg: isUz ? 'Xatolik yuz berdi. Qayta urinib ko\'ring.' : 'Произошла ошибка. Попробуйте снова.',
    required: isUz ? 'Majburiy maydon' : 'Обязательное поле',
    privacyConsent: isUz
      ? 'Shaxsiy ma\'lumotlarni qayta ishlashga roziman'
      : 'Я согласен(а) на обработку персональных данных',
    privacyConsentRequired: isUz
      ? 'Shaxsiy ma\'lumotlarni qayta ishlashga rozilik talab etiladi'
      : 'Необходимо согласие на обработку персональных данных',
  };

  const specialties = isUz
    ? [
        'Stomatologiya', 'Plastik jarrohlik', 'Ko\'z kasalliklari (LASIK/LASEK)',
        'Teri parvarishi va estetika', 'Ortopediya va bo\'g\'im', 'Umurtqa pog\'onasi',
        'Saraton davolash', 'Ginekologiya va bepushtlik', 'Anti-aging va ildiz hujayra',
        'Kompleks tibbiy tekshiruv', 'Proktologiya', 'Boshqa',
      ]
    : [
        'Стоматология', 'Пластическая хирургия', 'Офтальмология (LASIK/LASEK)',
        'Уход за кожей и эстетика', 'Ортопедия и суставы', 'Позвоночник',
        'Лечение рака', 'Гинекология и бесплодие', 'Анти-эйджинг и стволовые клетки',
        'Комплексное обследование', 'Проктология', 'Другое',
      ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!privacyChecked) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);
    setIsSubmitting(true);
    setIsError(false);

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6q323sddmmni7ck8al0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setIsSuccess(true);
        form.reset();
        setCharCount(0);
        setPrivacyChecked(false);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      <main className="flex-1 pt-28 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-700 text-sm font-medium rounded-full mb-4">
              MediVoyage
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {labels.pageTitle}
            </h1>
            <p className="text-base text-gray-500">{labels.pageSubtitle}</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-xl shadow-md p-8">
            {isSuccess ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{labels.successTitle}</h2>
                <p className="text-gray-500 mb-8 text-sm leading-relaxed">{labels.successMsg}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {labels.backHome}
                  </button>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {labels.sendAnother}
                  </button>
                </div>
              </div>
            ) : (
              <form
                id="consultation-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {labels.fullName} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder={labels.fullNamePlaceholder}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {isUz ? 'Elektron pochta' : 'Электронная почта'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={labels.emailPlaceholder}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {labels.phone} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder={labels.phonePlaceholder}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {labels.country}
                  </label>
                  <select
                    name="country"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition bg-white cursor-pointer"
                  >
                    <option value="">{labels.countryPlaceholder}</option>
                    <option value="uzbekistan">{labels.countryUz}</option>
                    <option value="russia">{labels.countryRu}</option>
                    <option value="kazakhstan">{labels.countryKz}</option>
                    <option value="other">{labels.countryOther}</option>
                  </select>
                </div>

                {/* Specialty */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {labels.specialty} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="specialty"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition bg-white cursor-pointer"
                  >
                    <option value="">{labels.specialtyPlaceholder}</option>
                    {specialties.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {labels.message}
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder={labels.messagePlaceholder}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{charCount}/500</p>
                </div>

                {/* Privacy Consent */}
                <div className={`flex items-start gap-2.5 p-3 rounded-md border ${privacyError ? 'border-red-300 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    id="consultation-privacy-consent"
                    checked={privacyChecked}
                    onChange={(e) => {
                      setPrivacyChecked(e.target.checked);
                      if (e.target.checked) setPrivacyError(false);
                    }}
                    className="mt-0.5 w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="consultation-privacy-consent" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                    {labels.privacyConsent} <span className="text-red-500">*</span>
                  </label>
                </div>
                {privacyError && (
                  <p className="text-xs text-red-500 -mt-2">{labels.privacyConsentRequired}</p>
                )}

                {isError && (
                  <p className="text-sm text-red-500">{labels.errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? labels.submitting : labels.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}